import Image from "next/image";
import Link from "next/link";

import type { Alternative } from "@/content/alternatives/schema";

interface AlternativesListProps {
  heading: string;
  alternatives: Alternative[];
}

export function AlternativesList({ heading, alternatives }: AlternativesListProps) {
  const rebolt = alternatives.find((a) => a.isRebolt);
  const others = alternatives.filter((a) => !a.isRebolt);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[1fr_2fr]">
          <h2 className="font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl lg:sticky lg:top-32">
            {heading}
          </h2>

          <div className="space-y-8">
            {rebolt && (
              <div className="flex items-center justify-between rounded-2xl border border-ash-200 bg-clay-100 px-6 py-5">
                <div className="flex items-center gap-4">
                  {rebolt.logo && (
                    <Image
                      src={rebolt.logo.src}
                      alt={rebolt.logo.alt}
                      width={32}
                      height={32}
                      className="size-8 object-contain"
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-ash-900">{rebolt.name}</h3>
                    <p className="mt-0.5 text-sm text-ash-500">{rebolt.tagline}</p>
                  </div>
                </div>
                <Link
                  href={rebolt.ctaHref}
                  className="shrink-0 rounded-full bg-rebolt-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-rebolt-600"
                >
                  {rebolt.ctaText}
                </Link>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {others.map((alt) => (
                <div key={alt.name} className="space-y-2">
                  <div className="flex items-center gap-3">
                    {alt.logo && (
                      <Image
                        src={alt.logo.src}
                        alt={alt.logo.alt}
                        width={28}
                        height={28}
                        className="size-7 object-contain"
                      />
                    )}
                    <h3 className="text-lg font-semibold text-ash-900">{alt.name}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-ash-500">{alt.tagline}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
